import React, { useState, useEffect } from 'react';
import { API_URL } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'sonner';
import {
    Loader2, CheckCircle2, Clock, Calendar as CalendarIcon,
    ChevronRight, ChevronLeft, Image as ImageIcon,
    Zap, Phone, Mail, Check
} from 'lucide-react';
import PoweredBy from '../components/ui/PoweredBy';

const PublicBookingPage = () => {
    const [step, setStep] = useState(1);
    const [services, setServices] = useState([]);
    const [selectedIds, setSelectedIds] = useState([]);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [slots, setSlots] = useState([]);
    const [selectedSlot, setSelectedSlot] = useState(null);
    const [loading, setLoading] = useState(false);

    // Nouveaux états UX
    const [companyConfig, setCompanyConfig] = useState({ name: "Chargement...", logo: null });
    const [emailHint, setEmailHint] = useState(null);
    const [consentToSave, setConsentToSave] = useState(false);
    const [isReturningClient, setIsReturningClient] = useState(false);

    const [formData, setFormData] = useState({
        name: '', email: '', phone: '', address: '', notes: ''
    });

    // Load Services & Config
    useEffect(() => {
        fetch(`${API_URL}/public/booking/services`)
            .then(res => res.json())
            .then(data => setServices(data))
            .catch(() => toast.error("Erreur lors du chargement des prestations"));

        fetch(`${API_URL}/settings/public-config`)
            .then(res => res.json())
            .then(data => setCompanyConfig({ name: data.company_name, logo: data.logo_base64 }))
            .catch(() => setCompanyConfig({ name: "Mon Entreprise", logo: null }));
    }, []);

    // Load Slots when date changes
    useEffect(() => {
        if (step === 2 && selectedIds.length > 0 && selectedDate) {
            const dateStr = format(selectedDate, 'yyyy-MM-dd');
            const idsParam = selectedIds.join(',');
            setLoading(true);
            fetch(`${API_URL}/public/booking/slots?date=${dateStr}&service_ids=${idsParam}`)
                .then(res => res.json())
                .then(setSlots)
                .finally(() => setLoading(false));
        }
    }, [selectedDate, selectedIds, step]);


    const checkEmail = async (email) => {
        if (!email.includes('@')) {
            setEmailHint(null);
            setIsReturningClient(false); // On reset si l'email change
            return;
        }
        try {
            const res = await fetch(`${API_URL}/public/booking/check-email?email=${email}`);
            const data = await res.json();
            setEmailHint(data.exists ? data.hint : null);
            if (!data.exists) setIsReturningClient(false);
        } catch (e) { console.error(e); }
    };

    // 🔴 LA FONCTION QUI GÈRE LA SÉLECTION (Corrigée pour être infaillible)
    const toggleService = (id) => {
        if (!id) return; // Sécurité
        setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const selectedServicesList = services.filter(s => selectedIds.includes(s.id || s._id));
    const totalDuration = selectedServicesList.reduce((acc, s) => acc + s.duration_minutes, 0);
    const totalHt = selectedServicesList.reduce((acc, s) => acc + s.price_ht, 0);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/public/booking/confirm`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    service_ids: selectedIds,
                    start_time: selectedSlot,
                    client_name: isReturningClient ? null : formData.name, // 👈 Null si connu
                    client_email: formData.email,
                    client_phone: isReturningClient ? null : formData.phone,
                    address: formData.address,
                    notes: formData.notes,
                    consent_to_save: consentToSave,
                    is_returning_client: isReturningClient // 👈 On prévient le backend
                })
            });
            if (res.ok) setStep(4);
            else toast.error("Erreur lors de la réservation");
        } catch { toast.error("Erreur serveur"); }
        finally { setLoading(false); }
    };

    if (step === 4) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
                <Card className="max-w-md w-full text-center p-8 border-none shadow-2xl bg-white text-slate-900">
                    <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-6 animate-in zoom-in duration-500" />
                    <h2 className="text-3xl font-bold mb-3 text-slate-900">Rendez-vous confirmé !</h2>
                    <p className="text-slate-600 mb-8 text-lg">Un email de confirmation contenant tous les détails vous a été envoyé.</p>
                    <Button onClick={() => window.location.reload()} className="w-full h-12 text-lg">Terminer</Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 flex flex-col items-center py-12 px-4 font-sans text-slate-900">

            {/* BRANDING HEADER CENTRÉ */}
            <div className="max-w-3xl w-full text-center mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
                <div className="flex justify-center items-center gap-4 mb-4">
                    {companyConfig.logo ? (
                        <img src={companyConfig.logo} alt="Logo Entreprise" className="h-16 w-auto object-contain drop-shadow-sm" />
                    ) : (
                        <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
                            <Zap className="w-7 h-7 text-white fill-white" />
                        </div>
                    )}
                    <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{companyConfig.name}</h1>
                </div>
                <p className="text-lg text-slate-600 max-w-xl mx-auto">
                    Réservez votre créneau en quelques clics. Sélectionnez vos prestations pour voir les disponibilités.
                </p>
            </div>

            <div className="max-w-5xl w-full grid md:grid-cols-3 gap-8">

                {/* SIDEBAR RECAP */}
                <Card className="md:col-span-1 h-fit shadow-xl border-slate-200/60 bg-white text-slate-900 sticky top-6 rounded-2xl overflow-hidden">
                    <CardContent className="p-6 space-y-4">
                        <h2 className="font-bold text-lg text-slate-900 border-b border-slate-100 pb-3">Votre Réservation</h2>

                        <div className="space-y-3">
                            {selectedServicesList.length > 0 ? (
                                selectedServicesList.map(s => (
                                    <div key={s.id} className="flex justify-between items-start text-sm">
                                        <div className="flex-1 pr-2">
                                            <p className="font-medium text-slate-800 leading-tight">{s.name}</p>
                                            <p className="text-xs text-slate-500">{s.duration_minutes} min</p>
                                        </div>
                                        <p className="font-semibold text-slate-900">{s.price_ht}€</p>
                                    </div>
                                ))
                            ) : (
                                <p className="text-sm text-slate-400 italic text-center py-4 bg-slate-50 rounded-lg">Aucune prestation sélectionnée</p>
                            )}

                            {selectedServicesList.length > 0 && (
                                <div className="pt-3 border-t border-slate-100 mt-3">
                                    <div className="flex justify-between items-center text-sm font-bold text-primary">
                                        <span>Total HT</span>
                                        <span>{totalHt.toFixed(2)}€</span>
                                    </div>
                                    <div className="flex justify-between items-center text-xs text-slate-500 mt-1">
                                        <span>Durée estimée</span>
                                        <span>{totalDuration} min</span>
                                    </div>
                                </div>
                            )}

                            {selectedSlot && (
                                <div className="mt-4 p-3 bg-primary/10 rounded-lg text-sm border border-primary/20 flex items-center gap-2 text-primary font-semibold">
                                    <CalendarIcon className="w-4 h-4" />
                                    {format(new Date(selectedSlot), "d MMMM à HH:mm", { locale: fr })}
                                </div>
                            )}
                        </div>

                        {/* CONTACT INFO */}
                        <div className="pt-6 mt-6 border-t border-slate-100 space-y-3">
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Besoin d'aide ?</p>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Phone className="w-4 h-4 text-slate-400" /> Numéro non renseigné
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-600">
                                <Mail className="w-4 h-4 text-slate-400" /> contact@{companyConfig.name.toLowerCase().replace(/\s+/g, '')}.com
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* MAIN CONTENT */}
                <Card className="md:col-span-2 shadow-xl border-slate-200/60 bg-white text-slate-900 rounded-2xl overflow-hidden">
                    <CardContent className="p-8">

                        {/* STEP 1: SERVICES */}
                        {step === 1 && (
                            <div className="space-y-6 animate-in fade-in duration-300">
                                <div className="flex justify-between items-end mb-6 border-b border-slate-100 pb-4">
                                    <div>
                                        <h3 className="text-2xl font-bold text-slate-900">Choisissez vos prestations</h3>
                                        <p className="text-sm text-slate-500 mt-1">Vous pouvez en sélectionner plusieurs.</p>
                                    </div>
                                    <Button disabled={selectedIds.length === 0} onClick={() => setStep(2)} className="shadow-md">
                                        Suivant <ChevronRight className="w-4 h-4 ml-2" />
                                    </Button>
                                </div>

                                <div className="grid gap-4">
                                    {/* 🔴 FIX : Une seule accolade ici */}
                                    {services.map(s => {
                                        const serviceId = s.id || s._id;
                                        const isSelected = selectedIds.includes(serviceId);

                                        return (
                                            <div
                                                key={serviceId}
                                                onClick={() => toggleService(serviceId)}
                                                className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 ${isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-slate-100 hover:border-slate-300 bg-white'}`}
                                            >
                                                <div className={`w-5 h-5 rounded flex-shrink-0 border flex items-center justify-center mr-4 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-slate-300 bg-white'}`}>
                                                    {isSelected && <Check className="w-3 h-3 text-white" />}
                                                </div>

                                                <div className="w-14 h-14 rounded-lg bg-slate-100 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center mr-4 shadow-sm">
                                                    {s.image_url ? (
                                                        <img src={s.image_url.startsWith('http') ? s.image_url : `${process.env.REACT_APP_BACKEND_URL || ''}${s.image_url}`} alt={s.name} className="w-full h-full object-cover" />
                                                    ) : <ImageIcon className="w-6 h-6 text-slate-300" />}
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <p className="font-bold text-lg text-slate-900 truncate">{s.name}</p>
                                                    <p className="text-sm text-slate-500 line-clamp-1">{s.description || 'Aucune description'}</p>
                                                </div>

                                                <div className="text-right ml-4 shrink-0">
                                                    <p className="font-bold text-lg text-slate-900">{s.price_ht}€ HT</p>
                                                    <p className="text-xs font-medium text-slate-500 flex items-center justify-end gap-1 mt-1"><Clock className="w-3 h-3" /> {s.duration_minutes} min</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {/* 🔴 FIX : Fin de la map avec une seule accolade */}
                                    {services.length === 0 && <p className="text-slate-500 text-center py-10 bg-slate-50 rounded-xl border border-dashed">Aucune prestation disponible pour le moment.</p>}
                                </div>
                            </div>
                        )}

                        {/* STEP 2: CALENDRIER */}
                        {step === 2 && (
                            <div className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                                <Button variant="ghost" size="sm" onClick={() => setStep(1)} className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-2"><ChevronLeft className="w-4 h-4 mr-1" /> Retour aux prestations</Button>

                                <div className="border-b border-slate-100 pb-4">
                                    <h3 className="text-2xl font-bold text-slate-900">Choisissez un créneau</h3>
                                    <p className="text-sm text-slate-500 mt-1">Durée prévue pour la prestation : <strong>{totalDuration} minutes</strong></p>
                                </div>

                                <div className="flex flex-col md:flex-row gap-8">
                                    <div className="border border-slate-200 rounded-xl p-3 shadow-sm bg-white text-slate-900">
                                        <Calendar
                                            mode="single" selected={selectedDate}
                                            onSelect={(d) => d && setSelectedDate(d)}
                                            locale={fr}
                                            disabled={(date) => date < new Date().setHours(0, 0, 0, 0)}
                                            className="rounded-md"
                                        />
                                    </div>
                                    <div className="flex-1 bg-slate-50 rounded-xl p-5 border border-slate-200">
                                        <p className="font-semibold mb-4 text-sm text-slate-800 flex items-center gap-2 border-b border-slate-200 pb-2">
                                            <CalendarIcon className="w-4 h-4 text-primary" />
                                            Disponibilités du {format(selectedDate, 'dd/MM/yyyy')}
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 max-h-[260px] overflow-y-auto pr-2 custom-scrollbar">
                                            {loading ? <div className="col-span-2 py-10 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div> :
                                                slots.length > 0 ? slots.map(slot => (
                                                    <Button
                                                        key={slot} variant="outline"
                                                        className="w-full bg-white border-slate-200 text-slate-700 hover:border-primary hover:text-primary hover:bg-primary/5 transition-all shadow-sm"
                                                        onClick={() => { setSelectedSlot(slot); setStep(3); }}
                                                    >
                                                        {format(new Date(slot), 'HH:mm')}
                                                    </Button>
                                                )) : <p className="col-span-2 text-center text-sm text-slate-500 py-10 bg-white rounded-lg border border-dashed border-slate-300">Aucun créneau assez long disponible ce jour.</p>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 3: FORMULAIRE */}
                        {step === 3 && (
                            <form onSubmit={handleSubmit} className="space-y-6 animate-in slide-in-from-right-4 duration-300">
                                <Button variant="ghost" size="sm" onClick={() => setStep(2)} className="pl-0 text-slate-500 hover:text-slate-900 -ml-2 mb-2" type="button"><ChevronLeft className="w-4 h-4 mr-1" /> Retour au calendrier</Button>

                                <div className="border-b border-slate-100 pb-4">
                                    <h3 className="text-2xl font-bold text-slate-900">Vos coordonnées</h3>
                                    <p className="text-sm text-slate-500 mt-1">Dernière étape avant la confirmation de votre rendez-vous.</p>
                                </div>

                                <div className="space-y-6">
                                    {/* EMAIL ET CONFIRMATION D'IDENTITÉ */}
                                    <div className="space-y-2">
                                        <Label className="text-slate-700">Email *</Label>
                                        <Input
                                            type="email" required placeholder="vous@exemple.com"
                                            className="bg-slate-50 focus:bg-white"
                                            value={formData.email}
                                            onChange={e => {
                                                setFormData({ ...formData, email: e.target.value });
                                                if (isReturningClient) setIsReturningClient(false);
                                            }}
                                            onBlur={(e) => checkEmail(e.target.value)}
                                        />

                                        {emailHint && !isReturningClient && (
                                            <div className="text-sm bg-blue-50 text-blue-800 p-3 rounded-xl border border-blue-200 mt-3 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm animate-in fade-in slide-in-from-top-2">
                                                <div className="flex items-center gap-2">
                                                    <span>✨</span> Ravi de vous revoir ! Êtes-vous <strong>{emailHint}</strong> ?
                                                </div>
                                                <Button size="sm" type="button" onClick={() => setIsReturningClient(true)} className="shrink-0 bg-blue-600 hover:bg-blue-700 text-white">
                                                    Oui, c'est moi
                                                </Button>
                                            </div>
                                        )}

                                        {isReturningClient && (
                                            <div className="text-sm bg-emerald-50 text-emerald-800 p-3 rounded-xl border border-emerald-200 mt-3 flex items-center justify-between shadow-sm animate-in fade-in">
                                                <div className="flex items-center gap-2">
                                                    <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                                                    <span>Identité confirmée ({emailHint}). Vous pouvez valider directement !</span>
                                                </div>
                                                <Button variant="ghost" size="sm" type="button" onClick={() => setIsReturningClient(false)} className="text-emerald-700 hover:text-emerald-800 hover:bg-emerald-100">
                                                    Modifier
                                                </Button>
                                            </div>
                                        )}
                                    </div>

                                    {!isReturningClient && (
                                        <div className="grid md:grid-cols-2 gap-6 animate-in fade-in">
                                            <div className="space-y-2">
                                                <Label className="text-slate-700">Nom Complet *</Label>
                                                <Input required placeholder="Ex: Jean Dupont" className="bg-slate-50 focus:bg-white" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label className="text-slate-700">Téléphone</Label>
                                                <Input type="tel" placeholder="Ex: 06 12 34 56 78" className="bg-slate-50 focus:bg-white" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label className="text-slate-700">Lieu de la prestation *</Label>
                                        <Input required placeholder="N°, rue, code postal et ville" className="bg-slate-50 focus:bg-white" value={formData.address} onChange={e => setFormData({ ...formData, address: e.target.value })} />
                                    </div>

                                    <div className="space-y-2">
                                        <Label className="text-slate-700">Informations complémentaires</Label>
                                        <Input className="bg-slate-50 focus:bg-white" value={formData.notes} onChange={e => setFormData({ ...formData, notes: e.target.value })} placeholder="Code porte, interphone, besoin spécifique..." />
                                    </div>

                                    {!isReturningClient && (
                                        <div className="p-4 bg-slate-50 hover:bg-slate-100 transition-colors rounded-xl border border-slate-200 flex items-start space-x-3 mt-8 cursor-pointer" onClick={() => setConsentToSave(!consentToSave)}>
                                            <input
                                                type="checkbox" id="consent"
                                                className="mt-0.5 h-5 w-5 rounded border-slate-300 text-primary focus:ring-primary cursor-pointer"
                                                checked={consentToSave}
                                                onChange={(e) => setConsentToSave(e.target.checked)}
                                                onClick={(e) => e.stopPropagation()}
                                            />
                                            <label htmlFor="consent" className="text-sm text-slate-700 leading-snug cursor-pointer select-none">
                                                J'accepte que mes informations soient conservées pour la création d'une fiche client et faciliter mes prochaines réservations.
                                            </label>
                                        </div>
                                    )}

                                    <div className="pt-6">
                                        <Button type="submit" className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" disabled={loading}>
                                            {loading ? <Loader2 className="animate-spin mr-2" /> : "Confirmer ma réservation"}
                                        </Button>
                                    </div>
                                </div>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
            <footer className="mt-16 text-slate-500"><PoweredBy /></footer>
        </div>
    );
};

export default PublicBookingPage;